import React, { useEffect, useState } from "react";
import { Cpu, MemoryStick, HardDrive, Network, Activity, RefreshCw } from "lucide-react";
import { showError } from "../../../utils/toast";
import baseapi from "../../../services/baseapi";

// LoadingSpinner placeholder
const LoadingSpinner = ({ section }: { section: string }) =>
    <div className="text-gray-200 text-xs mb-2 p-4">Loading {section}...</div>;

interface PerformanceMetricsProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
    platform: string;
}

const PerformanceMetrics: React.FC<PerformanceMetricsProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
    platform
}) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    async function fetchPerformance() {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id
                }),
            };
            const response = await baseapi.get("/asset_performance", { params });
            const raw = response.data?.data?.[0]?.results;

            const parsed =
                typeof raw === "string"
                    ? JSON.parse(raw)
                    : raw ?? null;

            setData(parsed);
        } catch (e: any) {
            showError(e.message);
        }
        setLoading(false);
    }
    useEffect(() => {

        fetchPerformance();
    }, [agent_id, asset_id, organization_id, client_id]);

    const isWindows = platform?.toLowerCase().includes("windows");

    const parsedData = data;

    // Derived CPU metrics
    const cpuProcessorTimePercent = parsedData?.cpu?.processorTimePercent;
    const cpuPrivilegedTimePercent = parsedData?.cpu?.privilegedTimePercent;
    const cpuUserTimePercent = parsedData?.cpu?.userTimePercent;
    const cpuContextSwitchesPerSec = parsedData?.cpu?.contextSwitchesPerSec;

    const cpuUsagePercent = parsedData?.cpu_usage_percent ?? parsedData?.cpu?.processorTimePercent;
    const cpuStatus = parsedData?.cpu_status ?? parsedData?.cpu?.status;
    const cpuLogicalProcessors = parsedData?.cpu_logical_processors ?? parsedData?.cpu?.cpu_logical_processors;
    const processCount = parsedData?.process_count ?? parsedData?.system?.processCount;

    // Derived Memory metrics
    const memoryAvailableMemoryMB = parsedData?.memory?.availableMemoryMB;
    const memoryCommittedMB = parsedData?.memory?.committedMB;
    const memoryPageFaultsPerSec = parsedData?.memory?.pageFaultsPerSec;

    const usedMB = parsedData?.memory?.usedMemoryMB !== undefined ? Number(parsedData.memory.usedMemoryMB) : NaN;
    const totalMB = parsedData?.memory?.totalMemoryMB !== undefined ? Number(parsedData.memory.totalMemoryMB) : NaN;
    const availMB = parsedData?.memory?.availableMemoryMB !== undefined ? Number(parsedData.memory.availableMemoryMB) : NaN;

    const memoryUsagePercent = parsedData?.memory_usage_percent 
        ?? (!isNaN(usedMB) && !isNaN(totalMB) && totalMB > 0
            ? Math.round((usedMB / totalMB) * 100)
            : parsedData?.memory?.memoryUsagePercent);
    const memoryUsedGb = parsedData?.memory_used_gb 
        ?? (!isNaN(usedMB) ? (usedMB / 1024).toFixed(2) : undefined);
    const memoryFreeGb = parsedData?.memory_free_gb 
        ?? (!isNaN(availMB) ? (availMB / 1024).toFixed(2) : undefined);
    const memoryTotalGb = parsedData?.memory_total_gb 
        ?? (!isNaN(totalMB) ? (totalMB / 1024).toFixed(2) : undefined);

    // Derived Disk metrics
    const diskReadBytesPerSec = parsedData?.disk?.diskReadBytesPerSec;
    const diskWriteBytesPerSec = parsedData?.disk?.diskWriteBytesPerSec;
    const avgDiskQueueLength = parsedData?.disk?.avgDiskQueueLength;

    const diskReadsPerSec = parsedData?.disk_reads_per_sec ?? parsedData?.disk?.diskReadsPerSec;
    const diskWritesPerSec = parsedData?.disk_writes_per_sec ?? parsedData?.disk?.diskWritesPerSec;
    const diskStatus = parsedData?.disk?.status ?? parsedData?.memory_status;

    // Derived Network & System metrics
    const sentKB = parsedData?.network?.bytesSentPerSecKB !== undefined ? Number(parsedData.network.bytesSentPerSecKB) : NaN;
    const receivedKB = parsedData?.network?.bytesReceivedPerSecKB !== undefined ? Number(parsedData.network.bytesReceivedPerSecKB) : NaN;

    const networkBytesTotalPerSecKB = parsedData?.network?.bytesTotalPerSecKB 
        ?? (!isNaN(sentKB) && !isNaN(receivedKB) 
            ? (sentKB + receivedKB) 
            : undefined);
    const networkBytesReceived = parsedData?.network_bytes_received 
        ?? (!isNaN(receivedKB) ? receivedKB * 1024 : undefined);
    const networkBytesSent = parsedData?.network_bytes_sent 
        ?? (!isNaN(sentKB) ? sentKB * 1024 : undefined);

    const uptimeSec = parsedData?.system?.systemUpTimeSeconds !== undefined ? Number(parsedData.system.systemUpTimeSeconds) : NaN;
    const uptimeDays = parsedData?.uptime_days 
        ?? (!isNaN(uptimeSec) ? Math.round(uptimeSec / 86400) : undefined);

    const isNewNetworkFormat = parsedData?.network?.bytesReceivedPerSecKB !== undefined;

    return (
        <div className="p-3">
            {loading && <LoadingSpinner section="performance" />}
            {!loading && (
                <>
                    <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <Activity className="w-4 h-4 text-white" />
                            </div>
                            <h2 className="text-sm font-bold text-white leading-tight">Performance Metrics</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => fetchPerformance()}
                                disabled={loading}
                                className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                                title="Refresh data"
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* CPU Performance */}
                        <div className="bg-gray-900/40 p-4 rounded-md border border-gray-700">
                            <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-wider">
                                <Cpu className="w-4 h-4 mr-2 text-orange-500" />
                                CPU Performance
                            </h3>
                            <div className="space-y-1.5 text-xs">
                                {isWindows ? (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Processor Time:</span>
                                            <span className="text-white font-mono">{cpuProcessorTimePercent ?? '--'}%</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Privileged Time:</span>
                                            <span className="text-white font-mono">{cpuPrivilegedTimePercent ?? '--'}%</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">User Time:</span>
                                            <span className="text-white font-mono">{cpuUserTimePercent ?? '--'}%</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Context Switches:</span>
                                            <span className="text-white font-mono">{cpuContextSwitchesPerSec ?? '--'}/sec</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">CPU Usage:</span>
                                            <span className="text-white font-mono">{cpuUsagePercent ?? '--'}%</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Status:</span>
                                            <span className={`font-mono font-bold ${cpuStatus === 'GOOD' ? 'text-green-400' : 'text-yellow-400'}`}>
                                                {cpuStatus ?? '--'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Logical Processors:</span>
                                            <span className="text-white font-mono">{cpuLogicalProcessors ?? '--'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Total Processes:</span>
                                            <span className="text-white font-mono">{processCount ?? '--'}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Memory Performance */}
                        <div className="bg-gray-900/40 p-4 rounded-md border border-gray-700">
                            <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-wider">
                                <MemoryStick className="w-4 h-4 mr-2 text-orange-500" />
                                Memory Performance
                            </h3>
                            <div className="space-y-1.5 text-xs">
                                {isWindows ? (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Available Memory:</span>
                                            <span className="text-white font-mono">{memoryAvailableMemoryMB ?? '--'} MB</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Committed Bytes:</span>
                                            <span className="text-white font-mono">{memoryCommittedMB ?? '--'} MB</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Page Faults:</span>
                                            <span className="text-white font-mono">{memoryPageFaultsPerSec ?? '--'}/sec</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Memory Usage:</span>
                                            <span className="text-white font-mono">{memoryUsagePercent ?? '--'}%</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Used Memory:</span>
                                            <span className="text-white font-mono">{memoryUsedGb ?? '--'} GB</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Free Memory:</span>
                                            <span className="text-white font-mono">{memoryFreeGb ?? '--'} GB</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Total Memory:</span>
                                            <span className="text-white font-mono">{memoryTotalGb ?? '--'} GB</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Disk Performance */}
                        <div className="bg-gray-900/40 p-4 rounded-md border border-gray-700">
                            <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-wider">
                                <HardDrive className="w-4 h-4 mr-2 text-orange-500" />
                                Disk Performance
                            </h3>
                            <div className="space-y-1.5 text-xs">
                                {isWindows ? (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Read Bytes:</span>
                                            <span className="text-white font-mono">{diskReadBytesPerSec ?? '--'} B/s</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Write Bytes:</span>
                                            <span className="text-white font-mono">{diskWriteBytesPerSec ?? '--'} B/s</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Avg Queue Length:</span>
                                            <span className="text-white font-mono">{avgDiskQueueLength ?? '--'}</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Disk Reads/Sec:</span>
                                            <span className="text-white font-mono">{diskReadsPerSec ?? '--'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Disk Writes/Sec:</span>
                                            <span className="text-white font-mono">{diskWritesPerSec ?? '--'}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Status:</span>
                                            <span className={`font-mono font-bold ${diskStatus === 'GOOD' ? 'text-green-400' : 'text-yellow-400'}`}>
                                                {diskStatus ?? '--'}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Network & System Performance */}
                        <div className="bg-gray-900/40 p-4 rounded-md border border-gray-700">
                            <h3 className="text-xs font-bold text-white mb-3 flex items-center uppercase tracking-wider">
                                <Network className="w-4 h-4 mr-2 text-orange-500" />
                                {isWindows ? "Network Performance" : "Network & System"}
                            </h3>
                            <div className="space-y-1.5 text-xs">
                                {isWindows ? (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-200 font-medium">Total Bytes:</span>
                                        <span className="text-white font-mono">{networkBytesTotalPerSecKB ?? '--'} KB/s</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Bytes Received:</span>
                                            <span className="text-white font-mono">
                                                {networkBytesReceived
                                                    ? (networkBytesReceived / (1024 * 1024)).toFixed(2) + (isNewNetworkFormat ? ' MB/s' : ' MB')
                                                    : '--'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">Bytes Sent:</span>
                                            <span className="text-white font-mono">
                                                {networkBytesSent
                                                    ? (networkBytesSent / (1024 * 1024)).toFixed(2) + (isNewNetworkFormat ? ' MB/s' : ' MB')
                                                    : '--'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-200 font-medium">System Uptime:</span>
                                            <span className="text-white font-mono">{uptimeDays ? `${uptimeDays} days` : '--'}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PerformanceMetrics;